import React from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Card, 
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import HelpIcon from '@mui/icons-material/Help';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BookIcon from '@mui/icons-material/Book';
import BugReportIcon from '@mui/icons-material/BugReport';
import FeedbackIcon from '@mui/icons-material/Feedback';
import EmailIcon from '@mui/icons-material/Email';
import DiscordIcon from '@mui/icons-material/Forum';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ConstructionIcon from '@mui/icons-material/Construction';

const HelpSupportPage: React.FC = () => {
  const navigate = useNavigate();

  const supportOptions = [
    {
      title: 'User Guide',
      icon: <BookIcon />,
      description: 'Comprehensive documentation on how to use CAPSized Fantasy',
      status: 'In Development',
      action: () => console.log('User Guide coming soon!')
    },
    {
      title: 'Contact Support',
      icon: <SupportAgentIcon />,
      description: 'Get help with technical issues or account problems',
      status: 'Coming Soon',
      action: () => console.log('Support system coming soon!')
    },
    {
      title: 'Report a Bug',
      icon: <BugReportIcon />,
      description: 'Found something that isn\'t working? Let us know!',
      status: 'Available',
      action: () => window.open('mailto:support@capsized-fantasy.com?subject=Bug Report', '_blank')
    },
    {
      title: 'Feature Requests',
      icon: <FeedbackIcon />,
      description: 'Suggest new features or improvements',
      status: 'Available',
      action: () => window.open('mailto:feedback@capsized-fantasy.com?subject=Feature Request', '_blank')
    }
  ];

  const commonQuestions = [
    {
      question: 'How do I join a league?',
      answer: 'League commissioners can invite you by sharing a league invite link. Click the link to join the league automatically.'
    },
    {
      question: 'When do waivers process?',
      answer: 'Waiver claims typically process on Tuesday nights, but this can vary by league settings. Check with your commissioner for specific timing.'
    },
    {
      question: 'How do trades work?',
      answer: 'Navigate to the Trade Builder to propose trades with other teams. All trades must be approved by both parties and may require commissioner approval depending on league settings.'
    },
    {
      question: 'What are rookie drafts?',
      answer: 'Dynasty leagues typically hold annual rookie drafts to draft incoming NFL players. The draft order is usually determined by the previous season\'s standings.'
    },
    {
      question: 'How is scoring calculated?',
      answer: 'Scoring is based on real NFL player performance. Each league may have different scoring settings configured by the commissioner.'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Available': return 'success';
      case 'In Development': return 'warning';
      case 'Coming Soon': return 'info';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <ConstructionIcon sx={{ fontSize: 48, color: 'warning.main', mr: 2 }} />
          <HelpIcon sx={{ fontSize: 48, color: 'primary.main' }} />
        </Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Help & Support
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          We're building a comprehensive support system
        </Typography>
        <Chip 
          label="🚧 Under Development" 
          color="warning" 
          variant="outlined"
          sx={{ fontSize: '1rem', py: 2 }}
        />
      </Paper>

      <Grid container spacing={3}>
        {/* Support Options */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
                Get Help
              </Typography>
              <List>
                {supportOptions.map((option, index) => (
                  <React.Fragment key={option.title}>
                    <ListItem 
                      sx={{ py: 2, cursor: option.status === 'Available' ? 'pointer' : 'default' }}
                      onClick={option.action}
                    >
                      <ListItemIcon sx={{ minWidth: 56 }}>
                        {option.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h6">{option.title}</Typography>
                            <Chip 
                              label={option.status} 
                              size="small"
                              color={getStatusColor(option.status) as any}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={option.description}
                      />
                    </ListItem>
                    {index < supportOptions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" component="h3" gutterBottom>
                Contact Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="support@capsized-fantasy.com" 
                    secondary="Technical support and account issues"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="feedback@capsized-fantasy.com" 
                    secondary="Feature requests and general feedback"
                  />
                </ListItem>
              </List>
              
              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="body2">
                  📧 <strong>Email Support:</strong> While we're building our comprehensive support system, 
                  email is the best way to reach us. We typically respond within 24 hours.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* FAQ Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
                Frequently Asked Questions
              </Typography>
              {commonQuestions.map((faq, index) => (
                <Accordion key={index} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary">
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
              
              <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="body2">
                  💡 <strong>More FAQs Coming:</strong> We're compiling a comprehensive FAQ section 
                  based on common questions from our users. Have a question? Send it our way!
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Coming Soon Features */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" component="h3" gutterBottom>
            Coming Soon to Help & Support
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>📚 Knowledge Base</Typography>
                <Typography variant="body2" color="text.secondary">
                  Searchable articles covering all aspects of fantasy football and app usage
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>💬 Live Chat</Typography>
                <Typography variant="body2" color="text.secondary">
                  Real-time support chat for immediate assistance during peak times
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>🎥 Video Tutorials</Typography>
                <Typography variant="body2" color="text.secondary">
                  Step-by-step video guides for complex features and strategies
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default HelpSupportPage;
